// V3 Records Console — documents + observations su /api/v2/records-*
// (cap_records). Dominio clinico/osservazionale: documenti versionati
// (uno per profile+kind) e osservazioni append-only.

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../auth/session';

type Row = Record<string, any>;

const card: React.CSSProperties = { background: 'var(--sk-card, #fff)', borderRadius: 12, padding: 16, border: '1px solid var(--sk-border, #e5e7eb)' };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--sk-border, #d1d5db)', fontSize: 14 };
const btn = (bg = '#111827'): React.CSSProperties => ({ padding: '7px 14px', borderRadius: 8, background: bg, color: '#fff', border: 0, cursor: 'pointer', fontSize: 13 });
const badge = (s: string): React.CSSProperties => ({
  padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
  background: s === 'completed' ? '#dcfce7' : s === 'draft' ? '#f3f4f6' : '#dbeafe',
  color: s === 'completed' ? '#166534' : s === 'draft' ? '#4b5563' : '#1e40af',
});

const DOC_KINDS = ['anamnesis', 'health_card', 'evaluation', 'consent'];
const OBS_KINDS = ['body_measurement', 'progress_log', 'medical_cert', 'treatment_history', 'custom'];

export function V3RecordsPage() {
  const [customers, setCustomers] = useState<Row[]>([]);
  const [profileId, setProfileId] = useState('');
  const [documents, setDocuments] = useState<Row[]>([]);
  const [observations, setObservations] = useState<Row[]>([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [docForm, setDocForm] = useState({ kind: 'anamnesis', content: '' });
  const [obsForm, setObsForm] = useState({ kind: 'body_measurement', value: '', unit: 'kg', note: '', metrics: '' });

  const loadCustomers = useCallback(async () => {
    try {
      const r = await apiFetch('/api/v2/customers');
      const data = await r.json();
      setCustomers(Array.isArray(data) ? data : data.customers || []);
    } catch { /* picker opzionale */ }
  }, []);

  const load = useCallback(async () => {
    if (!profileId) { setDocuments([]); setObservations([]); return; }
    try {
      const [d, o] = await Promise.all([
        apiFetch(`/api/v2/records-documents?profile_id=${profileId}`),
        apiFetch(`/api/v2/records-observations?profile_id=${profileId}`),
      ]);
      setDocuments(await d.json()); setObservations(await o.json());
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento');
    }
  }, [profileId]);

  useEffect(() => { void loadCustomers(); }, [loadCustomers]);
  useEffect(() => { void load(); }, [load]);

  const saveDoc = async () => {
    setSaving(true); setError('');
    try {
      let content: Record<string, unknown>;
      try { content = JSON.parse(docForm.content || '{}'); }
      catch { throw new Error('Content deve essere JSON valido'); }
      const res = await apiFetch('/api/v2/records-documents', {
        method: 'PUT',
        body: JSON.stringify({ profile_id: Number(profileId), kind: docForm.kind, status: 'completed', content }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText);
      setDocForm({ ...docForm, content: '' });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore documento');
    } finally {
      setSaving(false);
    }
  };

  const addObs = async () => {
    setSaving(true); setError('');
    try {
      let metrics: Record<string, unknown> = {};
      try { metrics = JSON.parse(obsForm.metrics || '{}'); }
      catch { throw new Error('Metrics deve essere JSON valido'); }
      const res = await apiFetch('/api/v2/records-observations', {
        method: 'POST',
        body: JSON.stringify({
          profile_id: Number(profileId), kind: obsForm.kind,
          value: obsForm.value ? Number(obsForm.value) : null,
          unit: obsForm.unit || null, note: obsForm.note || null, metrics,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText);
      setObsForm({ kind: obsForm.kind, value: '', unit: 'kg', note: '', metrics: '' });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore osservazione');
    } finally {
      setSaving(false);
    }
  };

  const profileName = customers.find((c) => String(c.id) === profileId)?.name
    || customers.find((c) => String(c.id) === profileId)?.display_name || '';

  return (
    <div style={{ display: 'grid', gap: 20, gridTemplateColumns: 'minmax(300px, 380px) 1fr' }}>
      <div style={{ display: 'grid', gap: 20, alignContent: 'start' }}>
        <div style={card}>
          <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Cliente</h3>
          <select style={input} value={profileId} onChange={(e) => setProfileId(e.target.value)}>
            <option value="">Seleziona profilo…</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name || c.display_name || `#${c.id}`}
              </option>
            ))}
          </select>
        </div>

        {profileId && (
          <>
            <div style={card}>
              <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Documento</h3>
              <div style={{ display: 'grid', gap: 10 }}>
                <select style={input} value={docForm.kind} onChange={(e) => setDocForm({ ...docForm, kind: e.target.value })}>
                  {DOC_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
                <textarea style={{ ...input, minHeight: 80, fontFamily: 'monospace' }}
                  placeholder='{"height_cm":180,"has_allergies":true,…}'
                  value={docForm.content} onChange={(e) => setDocForm({ ...docForm, content: e.target.value })} />
                <button style={btn()} disabled={saving} onClick={() => void saveDoc()}>
                  {saving ? '…' : 'Salva (upsert + version)'}
                </button>
              </div>
            </div>

            <div style={card}>
              <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Osservazione</h3>
              <div style={{ display: 'grid', gap: 10 }}>
                <select style={input} value={obsForm.kind} onChange={(e) => setObsForm({ ...obsForm, kind: e.target.value })}>
                  {OBS_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input style={input} placeholder="Valore" type="number" step="0.01" value={obsForm.value}
                    onChange={(e) => setObsForm({ ...obsForm, value: e.target.value })} />
                  <input style={{ ...input, width: 80 }} placeholder="unità" value={obsForm.unit}
                    onChange={(e) => setObsForm({ ...obsForm, unit: e.target.value })} />
                </div>
                <input style={input} placeholder='Metriche JSON {"chest_cm":98,…}' value={obsForm.metrics}
                  onChange={(e) => setObsForm({ ...obsForm, metrics: e.target.value })} />
                <input style={input} placeholder="Nota" value={obsForm.note}
                  onChange={(e) => setObsForm({ ...obsForm, note: e.target.value })} />
                <button style={btn('#0d6efd')} disabled={saving} onClick={() => void addObs()}>
                  {saving ? '…' : 'Aggiungi (append)'}
                </button>
              </div>
            </div>
          </>
        )}
        {error ? <p style={{ color: '#dc2626', fontSize: 13 }}>{error}</p> : null}
        <p className="sk-muted" style={{ fontSize: 12 }}>
          API: /api/v2 · {documents.length} documenti · {observations.length} osservazioni
        </p>
      </div>

      <div style={{ display: 'grid', gap: 20, alignContent: 'start' }}>
        <div style={card}>
          <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>
            Documenti {profileName ? `· ${profileName}` : ''} ({documents.length})
          </h4>
          {!profileId && <p className="sk-muted">Seleziona un cliente.</p>}
          {profileId && documents.length === 0 && <p className="sk-muted">Nessun documento.</p>}
          {documents.map((d) => (
            <div key={d.id} style={{ padding: '10px 0', borderTop: '1px solid var(--sk-border, #eee)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span><strong>{d.kind}</strong> · v{d.version}</span>
                <span style={badge(d.status)}>{d.status}</span>
              </div>
              <pre style={{ fontSize: 11, color: '#6b7280', margin: '4px 0 0', overflow: 'auto' }}>
                {JSON.stringify(d.content, null, 1)}
              </pre>
            </div>
          ))}
        </div>

        <div style={card}>
          <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Osservazioni ({observations.length})</h4>
          {!profileId && <p className="sk-muted">—</p>}
          {profileId && observations.length === 0 && <p className="sk-muted">Nessuna osservazione.</p>}
          {observations.slice(0, 20).map((o) => (
            <div key={o.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--sk-border, #eee)', fontSize: 13 }}>
              <span>
                <strong>{o.kind}</strong>
                {o.value != null ? ` · ${o.value} ${o.unit || ''}` : ''}
                {o.note ? ` · ${o.note}` : ''}
              </span>
              <span style={{ color: '#6b7280' }}>{String(o.observed_at).slice(0, 16).replace('T', ' ')}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
